import { DatePipe, DecimalPipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SimpleApi } from '@core/api/simple.api';
import { AuthService } from '@core/auth/auth.service';
import { RettighedsType } from '@core/auth/rettighed.model';
import { BooleanPipe } from '@core/pipes/boolean.pipe';
import { CprNummerPipe } from '@core/pipes/cprnummer.pipe';
import { PersonHelbredApi } from '@features/helbred/person-helbred/person-helbred.api';
import { PersonHelbred } from '@features/helbred/person-helbred/person-helbred.model';
import { KontoComponent } from '@features/konto/konto.component';
import { Kunde } from '@features/kunder/kunde.model';
import { GruppeProduktApi } from '@features/letregister/gruppe-produkt.api';
import { LetregisterService } from '@features/letregister/letregister.service';
import { LetsikringRevision } from '@features/letregister/letsikring-revision.model';
import { LetsikringRisiko } from '@features/letregister/letsikring-risiko.model';
import { DetailRowComponent } from '@shared/detail-row/detail-row.component';
import { KundeSelectorComponent } from '@shared/kunde-selector/kunde-selector.component';
import { LetDatePickerComponent } from '@shared/let-datepicker/let-date-picker.component';
import { Checkbox } from 'primeng/checkbox';
import { InputNumber } from 'primeng/inputnumber';
import { Select, SelectChangeEvent } from 'primeng/select';
import { Textarea } from 'primeng/textarea';
import { RapKode } from './rap-kode.model';
import { DisplayDecimalComponent } from '@shared/display-decimal/display-decimal.component';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { TableRowDirective } from '@core/primevue/directives/table-row.directive';
import { RisikoOpkraevning } from '@features/risikoopkraevning/risiko-opkraevning.model';
import { RisikoOpkraevningApi } from '@features/risikoopkraevning/risikoopkraevning-opkraevninger/risiko-opkraevning.api';
import { Letsikring } from '@features/letregister/letsikring.model';
import { RadioButton } from 'primeng/radiobutton';

@Component({
  selector: 'app-letsikring-risiko',
  imports: [
    DatePipe,
    BooleanPipe,
    DecimalPipe,
    CprNummerPipe,
    ReactiveFormsModule,
    Button,
    Select,
    Checkbox,
    Textarea,
    InputNumber,
    DetailRowComponent,
    KontoComponent,
    LetDatePickerComponent,
    KundeSelectorComponent,
    DisplayDecimalComponent,
    Dialog,
    TableModule,
    TableRowDirective,
    RadioButton,
    FormsModule,
  ],
  templateUrl: './letsikring-risiko.component.html',
  styleUrl: './letsikring-risiko.component.css',
})
export class LetsikringRisikoComponent {
  form = input.required<FormGroup>();
  editMode = input.required<boolean>();
  revision = input.required<LetsikringRevision>();
  letsikringRisiko = input.required<LetsikringRisiko>();
  letsikring = input.required<Letsikring>();

  simpleApi = inject(SimpleApi);
  personHelbredApi = inject(PersonHelbredApi);
  risikoOpkraevningApi = inject(RisikoOpkraevningApi);
  gruppeProduktApi = inject(GruppeProduktApi);

  letregisterService = inject(LetregisterService);
  authService = inject(AuthService);

  personHelbred = signal<PersonHelbred | undefined>(undefined);
  gruppeProduktOptions = signal<RapKode[]>([]);
  showOpkraevningsHistorik = signal(false);
  opkraevningsHistorik = signal<RisikoOpkraevning[]>([]);
  visRevision = signal<string>('alle');

  constructor() {
    effect(async () => {
      const personHelbredId = this.revision().letsikringRisiko?.personHelbredId;

      if (personHelbredId) {
        untracked(async () => {
          const personHelbred =
            await this.personHelbredApi.getById(personHelbredId);
          this.personHelbred.set(personHelbred);
        });
      }
    });

    effect(async () => {
      const produktType = this.revision().letsikringRisiko?.produktType;

      if (produktType) {
        untracked(async () => {
          const response = await this.simpleApi.getList<RapKode>(
            '/api/admin/letsikringer/muligerapkoder',
            {
              produktType: produktType,
            }
          );
          this.gruppeProduktOptions.set(response);
        });
      }
    });
  }

  allowStraksOpkraevning() {
    return (
      this.authService.hasRettighed(RettighedsType.AdminRisikoopkraevning) &&
      !this.revision().isLts
    );
  }

  getTilbudsnr() {
    return this.letregisterService.getTilbudsnr(this.revision());
  }

  getAftaledokument() {
    return this.revision().aftaleDokument;
  }

  pantsatTypeOptions = [
    { text: 'Ikke pantsat', value: 'IkkePantsat' },
    { text: 'Pantsat i pengeinstitut', value: 'Pengeinstitut' },
    { text: 'Pantsat i koncern', value: 'Koncern' },
  ];

  frekvensForIndbetalingOptions = [
    { text: 'Månedlig', value: 'Maanedlig' },
    { text: 'Årlig', value: 'Aarlig' },
  ];

  onForsikredeSelected(kunde: Kunde) {
    this.form().patchValue({
      gaeldendeRevision: {
        letsikringRisiko: {
          forsikredeId: kunde.id,
          forsikredeNavn: kunde.navn,
        },
      },
    });
  }

  onBarnSelected(kunde: Kunde) {
    this.form().patchValue({
      gaeldendeRevision: {
        letsikringRisiko: {
          barnId: kunde.id,
          barnNavn: kunde.navn,
        },
      },
    });
  }

  onGruppeProduktChange(event: SelectChangeEvent) {
    const gruppeProduktId = event.value;
    const rapKode = this.gruppeProduktOptions().find(
      x => x.gruppeProduktId == gruppeProduktId
    );
    this.form().get('gaeldendeRevision')?.get('letsikringRisiko')?.patchValue({
      _pris: rapKode?.pris,
      _ydelse: rapKode?.ydelse,
    });
  }

  getNyYdelse = computed(() => {
    console.log('getNyYdelse');
    console.log(
      this.form().get('gaeldendeRevision')?.get('letsikringRisiko')?.value
    );
    return this.form().value.gaeldendeRevision?.letsikringRisiko._ydelse;
  });

  async visOpkraevningshistorik() {
    this.showOpkraevningsHistorik.set(true);
    const response = await this.risikoOpkraevningApi.getList({
      filter: JSON.stringify({
        letsikringNr: this.letsikring()?.letsikringNr,
      }),
      sortBy: 'periodeFra',
      descending: true,
    });
    if (response.success) {
      this.opkraevningsHistorik.set(response.data);
    }
    return [];
  }

  letsikringNr = computed(() => this.letsikring()?.letsikringNr);

  opkraevningsHistorikFiltered = computed(() => {
    const visning = this.visRevision();
    if (visning === 'alle') {
      return this.opkraevningsHistorik();
    } else {
      return this.opkraevningsHistorik().filter(
        x => x.revisionNr === this.revision().revisionNr
      );
    }
  });
}
